import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, HelpCircle, History, CircleDot } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'
import useAppStore from '../../../store/useAppStore'
import useInventoryStore from '../../../store/useInventoryStore'
import DoubleWheel from '../components/DoubleWheel'
import DoubleBetModal from '../components/DoubleBetModal'
import DoubleBetsFeed from '../components/DoubleBetsFeed'
import DoubleWinnerSummary from '../components/DoubleWinnerSummary'
import HelpModal from '../../../components/HelpModal'

const formatTimer = (millisecondsValue) => {
  const safeMilliseconds = Math.max(0, Number(millisecondsValue || 0))
  const totalTenths = Math.floor(safeMilliseconds / 100)
  const seconds = Math.floor(totalTenths / 10)
  const tenths = totalTenths % 10
  return `${seconds}.${tenths}`
}

const getStatusPollDelay = (status) => {
  if (status === 'raffle') return 300
  if (status === 'finished') return 300
  if (status === 'result') return 300
  return 1000
}

const Double = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    activeRound,
    inventory,
    balance,
    isBetting,
    loadActiveRound,
    loadBetForm,
    placeBet
  } = useDoubleStore()
  const { user, loadProfile } = useAppStore()
  const { items: inventoryItems, loadInventory } = useInventoryStore((state) => ({
    items: state.items,
    loadInventory: state.loadInventory
  }))

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)
  const [countdownMs, setCountdownMs] = useState(Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000))

  const pollTimeoutRef = useRef(null)
  const timerIntervalRef = useRef(null)
  const abortControllerRef = useRef(null)
  const countdownTargetTsRef = useRef(null)
  const lastCountdownSyncRef = useRef({ roundId: null, status: null })
  const betDataRefreshPromiseRef = useRef(null)
  const betDataIntervalRef = useRef(null)
  const [isBetDataReady, setIsBetDataReady] = useState(false)

  const roundId = activeRound?.id
  const isRoundReady = Boolean(roundId)
  const roundStatus = activeRound?.status || null
  const bets = Array.isArray(activeRound?.bets) ? activeRound.bets : []
  const sums = {
    red: Number(activeRound?.sum_red || 0),
    black: Number(activeRound?.sum_black || 0),
    green: Number(activeRound?.sum_green || 0)
  }
  const myColorSums = useMemo(() => {
    return bets.reduce(
      (acc, bet) => {
        if (!bet?.your_bet) return acc

        const color = bet?.color
        if (!['red', 'black', 'green'].includes(color)) return acc

        acc[color] += Number(bet?.sum || 0)
        return acc
      },
      { red: 0, black: 0, green: 0 }
    )
  }, [bets])
  const stats100 = activeRound?.stats_100 || {}
  const modalInventory = useMemo(() => {
    const hasDoubleInventory = Array.isArray(inventory) && inventory.length > 0

    if (hasDoubleInventory) {
      return inventory
    }

    return (inventoryItems || [])
      .filter((item) => item.status === 'available' || item.status === 'returned')
      .map((item) => ({
        id: item.id,
        name: item.win?.name || item.name || `Item #${item.id}`,
        img: item.win?.img || item.img || '',
        cost: Number(item.win?.cost ?? item.cost ?? item.price ?? 0)
      }))
  }, [inventory, inventoryItems])

  const isBetLocked = !isRoundReady || ['raffle', 'result', 'finished'].includes(roundStatus)
  const currentBalance = Number(balance || user?.balance || 0)

  const handleStopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const startPolling = useCallback(async () => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const round = await loadActiveRound(controller.signal)
      const nextDelay = getStatusPollDelay(round?.status)
      pollTimeoutRef.current = setTimeout(startPolling, nextDelay)
    } catch (error) {
      pollTimeoutRef.current = setTimeout(startPolling, 1500)
    }
  }, [loadActiveRound])

  useEffect(() => {
    startPolling()
    return () => {
      handleStopPolling()
    }
  }, [startPolling])

  useEffect(() => {
    if (!activeRound?.id) return
    setIsBetModalOpen(false)
  }, [activeRound?.id])

  useEffect(() => {
    const nextRoundId = activeRound?.id || null
    const nextStatus = activeRound?.status || null
    const nextMilliseconds = Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000)
    const prevSync = lastCountdownSyncRef.current

    const isNewRound = prevSync.roundId !== nextRoundId
    const enteredBetStatus = !['new', 'started'].includes(prevSync.status) && ['new', 'started'].includes(nextStatus)

    if ((isNewRound || enteredBetStatus) && ['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = Date.now() + nextMilliseconds
      setCountdownMs(nextMilliseconds)
    }

    if (!['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = null
    }

    lastCountdownSyncRef.current = {
      roundId: nextRoundId,
      status: nextStatus
    }
  }, [activeRound?.id, activeRound?.status, activeRound?.second_to_raffle])

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (!['new', 'started'].includes(roundStatus)) return

    timerIntervalRef.current = setInterval(() => {
      if (!countdownTargetTsRef.current) return

      const remainingMs = Math.max(0, countdownTargetTsRef.current - Date.now())
      setCountdownMs(remainingMs)

      if (remainingMs <= 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }, 33)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [roundStatus])

  const refreshBetData = useCallback(async ({ showError = false } = {}) => {
    if (betDataRefreshPromiseRef.current) {
      return betDataRefreshPromiseRef.current
    }

    betDataRefreshPromiseRef.current = (async () => {
      try {
        await loadBetForm()
        setIsBetDataReady(true)

        Promise.allSettled([
          loadProfile(),
          loadInventory()
        ]).catch(() => {})

        return true
      } catch (error) {
        if (showError) {
          toast.error(t('double.toasts.betFormError', 'Не удалось загрузить форму ставки'))
        }
        return false
      } finally {
        betDataRefreshPromiseRef.current = null
      }
    })()

    return betDataRefreshPromiseRef.current
  }, [loadBetForm, loadInventory, loadProfile, t])

  useEffect(() => {
    refreshBetData()

    if (betDataIntervalRef.current) {
      clearInterval(betDataIntervalRef.current)
      betDataIntervalRef.current = null
    }

    betDataIntervalRef.current = setInterval(() => {
      refreshBetData()
    }, 15000)

    return () => {
      if (betDataIntervalRef.current) {
        clearInterval(betDataIntervalRef.current)
        betDataIntervalRef.current = null
      }
    }
  }, [refreshBetData])

  const handleOpenBetModal = async (color) => {
    if (isBetLocked) return

    const prepared = isBetDataReady ? true : await refreshBetData({ showError: true })
    if (!prepared) return

    setSelectedColor(color)
    setIsBetModalOpen(true)
  }

  const handleSubmitBet = async ({ color, crystal, items }) => {
    if (!roundId) {
      toast.error(t('double.toasts.roundUnavailable', 'Раунд недоступен'))
      return
    }

    try {
      await placeBet({ roundId, color, crystal, items })

      await Promise.allSettled([
        loadActiveRound(),
        refreshBetData()
      ])

      toast.success(t('double.toasts.betQueued', 'Ставка принята в очередь'))
      setIsBetModalOpen(false)
    } catch (error) {
      if (error.message === 'not enough balance') {
        toast.error(t('double.toasts.notEnoughBalance', 'Недостаточно кристаллов'))
        return
      }
      if (error.message === 'error sum') {
        toast.error(t('double.toasts.minBet', 'Минимальная ставка 100 кристаллов'))
        return
      }
      if (error.message === 'error max bet') {
        toast.error(t('double.toasts.maxBet', 'Превышен лимит 100 000 кристаллов'))
        return
      }
      toast.error(t('double.toasts.betError', 'Не удалось отправить ставку'))
    }
  }

  const statsRows = useMemo(() => {
    const total = Number(stats100.red || 0) + Number(stats100.black || 0) + Number(stats100.green || 0)
    const safeTotal = total > 0 ? total : 1

    return [
      { key: 'red', label: t('double.colors.red', 'Красный'), value: Number(stats100.red || 0), colorClass: 'bg-red-500' },
      { key: 'black', label: t('double.colors.black', 'Чёрный'), value: Number(stats100.black || 0), colorClass: 'bg-slate-300' },
      { key: 'green', label: t('double.colors.green', 'Зелёный'), value: Number(stats100.green || 0), colorClass: 'bg-emerald-400' }
    ].map((item) => ({
      ...item,
      percent: (item.value / safeTotal) * 100
    }))
  }, [stats100, t])

  const helpSteps = useMemo(() => ([
    t('double.help.step1', '1) Выберите цвет ставки: красный или чёрный дают x2, зелёный даёт x14.'),
    t('double.help.step2', '2) Добавьте кристаллы и/или предметы, минимальная ставка — 100 кристаллов.'),
    t('double.help.step3', '3) Дождитесь розыгрыша: колесо определит сектор и выигравший цвет раунда.')
  ]), [t])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-hidden">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary">
              <CircleDot className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('double.title', 'Double')}</span>
            </h1>
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              aria-label={t('double.help.button', 'Справка по режиму Double')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_350px]">
          <div className="space-y-6">
            {isRoundReady ? (
              <DoubleWheel
                roundId={roundId}
                status={roundStatus}
                prevSector={activeRound?.prev_round?.win_sector}
                winSector={activeRound?.win_sector}
                winColor={activeRound?.win_color}
                countdownMs={countdownMs}
                raffleSeconds={activeRound?.second_to_finish_raffle}
              />
            ) : (
              <div className="rounded-2xl p-6">
                <div className="mx-auto aspect-square w-full max-w-[340px] rounded-full border border-white/10 bg-dark-primary/70 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="h-8 w-8 rounded-full border-2 border-accent-primary/70 border-t-transparent animate-spin" />
                  </div>
                </div>
              </div>
            )}

            {isRoundReady && ['result', 'finished'].includes(roundStatus) && (
              <DoubleWinnerSummary
                bets={bets}
                winColor={activeRound?.win_color}
                winSector={activeRound?.win_sector}
              />
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('red')}
                className="relative overflow-hidden rounded-2xl border border-red-300/45 bg-gradient-to-br from-red-500/50 via-rose-500/30 to-red-900/45 px-4 py-3 text-left shadow-[0_8px_24px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-red-200/70 hover:shadow-[0_14px_34px_rgba(239,68,68,0.45)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-red-50 font-semibold tracking-wide">{t('double.colors.red', 'Красное')} ×2</p>
                    {myColorSums.red > 0 && (
                      <p className="mt-1 text-xs font-semibold text-red-200/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.red.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-red-100 text-sm inline-flex items-center gap-1 font-medium">
                    {sums.red.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('black')}
                className="relative overflow-hidden rounded-2xl border border-slate-300/35 bg-gradient-to-br from-slate-700/70 via-slate-900/80 to-black/90 px-4 py-3 text-left shadow-[0_8px_24px_rgba(2,6,23,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-200/70 hover:shadow-[0_14px_34px_rgba(2,6,23,0.75)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.14),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-100 font-semibold tracking-wide">{t('double.colors.black', 'Чёрное')} ×2</p>
                    {myColorSums.black > 0 && (
                      <p className="mt-1 text-xs font-semibold text-slate-100/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.black.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-slate-200 text-sm inline-flex items-center gap-1 font-medium">
                    {sums.black.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('green')}
                className="relative overflow-hidden rounded-2xl border border-emerald-300/45 bg-gradient-to-br from-emerald-400/55 via-emerald-500/35 to-emerald-900/50 px-4 py-3 text-left shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200/70 hover:shadow-[0_14px_34px_rgba(16,185,129,0.45)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-emerald-50 font-semibold tracking-wide">{t('double.colors.green', 'Зелёное')} ×14</p>
                    {myColorSums.green > 0 && (
                      <p className="mt-1 text-xs font-semibold text-emerald-200/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.green.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-emerald-100 text-sm inline-flex items-center gap-1 font-medium">
                    {sums.green.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>
            </div>

            <DoubleBetsFeed bets={bets} />
          </div>

          <div className="space-y-6 lg:sticky lg:top-0 h-fit">
            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
              <h3 className="text-white font-semibold mb-3">{t('double.stats100.title', '100 последних раундов')}</h3>
              <div className="space-y-3">
                {statsRows.map((row) => (
                  <div key={row.key}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-text-secondary">{row.label}</span>
                      <span className="text-white">{row.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className={`${row.colorClass} h-full`} style={{ width: `${row.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
                type="button"
                onClick={() => navigate('/double/history')}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">{t('double.buttons.history', 'История')}</span>
            </button>

          </div>
        </div>
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('double.help.title', 'Как работает Double')}
        steps={helpSteps}
        highlightLastStep={false}
      />

      <DoubleBetModal
        isOpen={isBetModalOpen}
        color={selectedColor}
        isLoading={isBetting}
        balance={currentBalance}
        inventory={modalInventory}
        onClose={() => setIsBetModalOpen(false)}
        onSubmit={handleSubmitBet}
      />
    </div>
  )
}

export default Double
